import { ToolCallIcon } from "@/components/ToolCall/ToolCallIcon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { WorkspaceHeaderButton } from "@/layouts/WorkspaceLayout/components/WorkspaceHeaderButton";
import { cn } from "@/utils";
import { ToolCall } from "@cohesive/api-client";
import { getInitials } from "@cohesive/ui-components";
import {
  useInboxNotifications,
  useMarkAllInboxNotificationsAsRead,
  useUnreadInboxNotificationsCount,
  useUser,
} from "@liveblocks/react";
import type { InboxNotificationCustomKindProps } from "@liveblocks/react-ui";
import {
  InboxNotification,
  InboxNotificationList,
  LiveblocksUiConfig,
} from "@liveblocks/react-ui";
import "@liveblocks/react-ui/styles.css";
// Dark-theme overrides keyed on the `.dark` class the app sets on <html>
// (ThemeContext), so the Liveblocks-rendered list follows the theme toggle.
import "@liveblocks/react-ui/styles/dark/attributes.css";
import { Bell, Bot, ShieldQuestion, type LucideIcon } from "lucide-react";
import {
  useMemo,
  useState,
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ComponentType,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
// Scoped tightening + retint of the Liveblocks list. Loaded after the two
// Liveblocks stylesheets above so these overrides win in light and dark.
import "./notification-bell.css";

/**
 * In-app notification bell for the workspace header. Reads Liveblocks inbox
 * notifications at project scope (the top-level LiveblocksProvider makes the
 * hooks work without a room) and renders the custom `$`-kinds triggered by the
 * backend's shared/notify package. Click-through deep-links via react-router.
 *
 * The accept/reject inline actions for $toolConfirmation are a deliberate
 * follow-up — clicking through to the chat handles confirmation for now.
 */

/** The latest activity carries the current headline for a batched notification. */
function latest<K extends keyof Liveblocks["ActivitiesData"]>(
  props: InboxNotificationCustomKindProps<K>,
) {
  const activities = props.inboxNotification.activities;
  return activities[activities.length - 1]?.data;
}

/**
 * Bold a known substring (e.g. the chat name) within an otherwise plain title.
 * The <strong> inherits Liveblocks' title-emphasis weight. Falls back to the
 * plain title when there's no hint or it isn't found.
 */
function emphasizeTitle(title: string, emphasis: string): ReactNode {
  if (!emphasis) return title;
  const index = title.indexOf(emphasis);
  if (index === -1) return title;
  return (
    <>
      {title.slice(0, index)}
      <strong>{emphasis}</strong>
      {title.slice(index + emphasis.length)}
    </>
  );
}

/** Shared custom-kind row: a per-kind aside + backend title/body + deep link. */
function renderRow<K extends keyof Liveblocks["ActivitiesData"]>(
  aside: ReactNode,
  props: InboxNotificationCustomKindProps<K>,
) {
  const data = latest(props);
  const title = data?.title ?? "Notification";
  const emphasis = data && "title_emphasis" in data ? data.title_emphasis : "";
  return (
    <InboxNotification.Custom
      {...props}
      title={emphasizeTitle(title, emphasis)}
      href={data?.path || undefined}
      markAsReadOnClick
      aside={aside}
    >
      {data?.body}
    </InboxNotification.Custom>
  );
}

/** A lucide glyph in Liveblocks' round icon slot — the default aside. */
function iconAside(Icon: LucideIcon): ReactNode {
  return (
    <InboxNotification.Icon>
      <Icon className="size-4 text-n-60" />
    </InboxNotification.Icon>
  );
}

/** The sharer's profile picture (initials fallback) for $resourceShared. */
function SharerAvatar({ userId, name }: { userId: string; name: string }) {
  const { user } = useUser(userId);
  const displayName = user?.name || name;
  return (
    <Avatar size="sm">
      <AvatarImage src={user?.avatar} alt={displayName} />
      <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
    </Avatar>
  );
}

/** The per-call icon fields the backend sends in the $toolConfirmation activityData. */
type ToolIcon = {
  function_name: string;
  platform_name: string;
  platform_display_name?: string;
  platform_image_url?: string;
};

/** Parse the tool_icons JSON array, tolerating malformed / empty input. */
function parseToolIcons(json: string): ToolIcon[] {
  if (!json) return [];
  try {
    const parsed: unknown = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as ToolIcon[]) : [];
  } catch {
    return [];
  }
}

/**
 * Reconstruct the minimal ToolCall shape ToolCallIcon reads, so the bell renders
 * exactly the icon the chat shows (function.name catches internal tools like the
 * e2b bash command icon; the platform fields drive the integration logos).
 */
function toolCallFromIcon(icon: ToolIcon): ToolCall {
  return {
    function: {
      name: icon.function_name,
      platform_name: icon.platform_name,
      platform_display_name: icon.platform_display_name,
      platform_image_url: icon.platform_image_url,
    },
  } as unknown as ToolCall;
}

/**
 * The pending calls' icons for $toolConfirmation: the first call's icon (identical
 * to the chat's, via the shared ToolCallIcon), with a "+N" badge when more than
 * one is pending. Chat icons are ~24px, so a side-by-side stack won't fit the
 * compact column — the badge conveys the count instead.
 */
function ToolConfirmationAside({ toolIcons }: { toolIcons: string }) {
  const icons = parseToolIcons(toolIcons);
  const extra = icons.length - 1;
  return (
    <div className="relative">
      <InboxNotification.Icon>
        {icons[0] ? (
          <ToolCallIcon toolCall={toolCallFromIcon(icons[0])} />
        ) : (
          <ShieldQuestion className="size-4 text-n-60" />
        )}
      </InboxNotification.Icon>
      {extra > 0 && (
        <span className="absolute -bottom-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-popover bg-n-20 px-1 text-[9px] font-semibold leading-none text-n-70">
          +{extra}
        </span>
      )}
    </div>
  );
}

const NOTIFICATION_KINDS: NonNullable<
  ComponentProps<typeof InboxNotification>["kinds"]
> = {
  $resourceShared: (props) => {
    const data = latest(props);
    return renderRow(
      <SharerAvatar
        userId={data?.actor_id ?? ""}
        name={data?.actor_name ?? ""}
      />,
      props,
    );
  },
  $toolConfirmation: (props) => {
    const data = latest(props);
    return renderRow(
      <ToolConfirmationAside toolIcons={data?.tool_icons ?? ""} />,
      props,
    );
  },
  $agentNotification: (props) => renderRow(iconAside(Bot), props),
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { inboxNotifications, isLoading, error } = useInboxNotifications();
  const { count } = useUnreadInboxNotificationsCount();
  const markAllAsRead = useMarkAllInboxNotificationsAsRead();

  const unread = count ?? 0;

  // Route deep links through react-router (and close the popover) instead of a
  // full-page navigation. External / modified clicks fall through to the anchor.
  // Memoized so InboxNotification doesn't remount its anchor subtree each render.
  const Anchor: ComponentType<ComponentPropsWithoutRef<"a">> = useMemo(
    () =>
      function NotificationAnchor({ href, onClick, ...props }) {
        return (
          <a
            href={href}
            onClick={(e) => {
              onClick?.(e);
              if (
                href?.startsWith("/") &&
                !e.defaultPrevented &&
                !e.metaKey &&
                !e.ctrlKey &&
                !e.shiftKey &&
                e.button === 0
              ) {
                e.preventDefault();
                setOpen(false);
                navigate(href);
              }
            }}
            {...props}
          />
        );
      },
    [navigate],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <WorkspaceHeaderButton
          icon={Bell}
          label="Notifications"
          pressed={open}
          className="relative"
        >
          <Bell className="size-4" />
          {unread > 0 && (
            <span
              aria-label={`${unread} unread notifications`}
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rust px-1 text-[10px] font-semibold leading-none text-white"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </WorkspaceHeaderButton>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="notification-popover w-96 p-0"
        // Don't pull focus into the list on open — Radix would otherwise focus
        // the first notification's actions button.
        onOpenAutoFocus={(e) => e.preventDefault()}
        // Liveblocks' per-notification "..." menu portals to document.body,
        // outside this popover, so it isn't clipped by the popover's bounds.
        // Interacting with it (opening it, clicking Delete / Mark as read) would
        // otherwise read as an outside interaction and dismiss the popover — keep
        // it open when the interaction targets a Liveblocks floating layer.
        onInteractOutside={(event) => {
          const target = event.detail.originalEvent.target as Element | null;
          if (target?.closest(".lb-elevation, .lb-dropdown, .lb-tooltip")) {
            event.preventDefault();
          }
        }}
      >
        <div className="flex items-center justify-between border-b border-n-10 px-3 py-2">
          <span className="text-sm font-semibold text-n-90">Notifications</span>
          <button
            type="button"
            onClick={() => markAllAsRead()}
            disabled={unread === 0}
            className={cn(
              "rounded text-xs text-n-50 transition-colors hover:text-n-80",
              unread === 0
                ? "cursor-default opacity-40 hover:text-n-50"
                : "cursor-pointer",
            )}
          >
            Mark all as read
          </button>
        </div>

        <ScrollArea className="max-h-[70vh]">
          {isLoading ? (
            <div className="px-3 py-8 text-center text-sm text-n-50">
              Loading…
            </div>
          ) : error ? (
            <div className="px-3 py-8 text-center text-sm text-n-50">
              Couldn&apos;t load notifications.
            </div>
          ) : inboxNotifications.length === 0 ? (
            <div className="flex flex-col items-center gap-1 px-3 py-10 text-center">
              <Bell className="size-5 text-n-40" />
              <span className="text-sm text-n-50">
                You&apos;re all caught up
              </span>
            </div>
          ) : (
            <LiveblocksUiConfig components={{ Anchor }}>
              <InboxNotificationList>
                {inboxNotifications.map((notification) => (
                  <InboxNotification
                    key={notification.id}
                    inboxNotification={notification}
                    kinds={NOTIFICATION_KINDS}
                  />
                ))}
              </InboxNotificationList>
            </LiveblocksUiConfig>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
